import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import { Button, Modal } from "antd";
import { openUrl } from "@/utils";
import { useGlobalStore, useOrderSettingStore } from "@/store/hooks";
import useAuthorityCheck from "@/hooks/useAuthorityCheck";
import Authority from "@/enum/Authority";
import { useModalStore } from "@/hooks/Modal/hooks";
import PrintWaybillModal from "../../dialogs/PrintWaybillDialog/PrintWaybillModal";
import ShopType from "@/enum/ShopType";
import MultiPlatformPrintWaybillModal from "@/scenes/OrderManagementForAllShop/components/dialogs/MultiPlatformPrintWaybillModal";
import { isAnyOrderWithAliOutTag } from "@/utils/OrderTagUtils";
import { uniq } from "lodash";
import { checkShopIdsEnabledAutoPrint } from "@/scenes/more/AutoPrint/services";
import OrderSellerRemarksDTO from "@/dto/order/OrderSellerRemarksDTO";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import { useOrderManagementStore } from "@/scenes/OrderManagementForAllShop/stores/OrderManagementForAllShopStore";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import OrderTabNameMap from "@/scenes/OrderManagement/map/OrderTabNameMap";
import { checkBeforePrint } from "../printCheck";
import WiredOrderTipModal from "../WiredOrderTipModal";
import { LoggerInfoTag, postLogInfo } from "@/services/api/log";
import type SupplierPartnershipsStore from "@/store/SupplierPartnershipsStore";
import { isSupportedOtherDropShippingSupplierSourceShopType } from "@/scenes/OrderManagementForAllShop/components/dialogs/MultiPlatformPrintWaybillModal/utils/otherDropShippingSupplier";

interface UsePrintWaybillClickParams {
    getSelectedOrders: () => OrderDTO[];

    onShipTaskDone?(): any;

    onUpdateOrder(orderId: number, order: Partial<OrderDTO>): void;

    /**
     * 传入此参数代表使用多店铺打单弹窗
     */
    multiPlatformModalOrderOriginCheck?: (order: OrderDTO) => ShopType;

    onRefreshOrders?: () => void;

    onUpdateOrderSellerRemarks?: (orderId: number, sellerRemarks: OrderSellerRemarksDTO) => void;
    onUpdateSupplierOrderRemarks?: (supplierOrderId: number, dropshippingRemarks) => void;

    showProcessInRange?: boolean;

    supplierPartnershipsStore?: SupplierPartnershipsStore;
}

function buildCustomConfirmProps(data: {
    getModalInstance: () => ReturnType<typeof Modal.confirm>;
    onNext: () => void;
    onFilterNext: () => void;
}) {
    return {
        closable: true,
        content: (
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: "-20px",
                    marginTop: 40,
                }}
            >
                <Button
                    className="mr10"
                    onClick={() => {
                        const instance = data.getModalInstance();

                        instance.destroy();

                        data.onNext();
                    }}
                >
                    继续打印
                </Button>

                <Button
                    type="primary"
                    onClick={() => {
                        const instance = data.getModalInstance();

                        instance.destroy();

                        data.onFilterNext();
                    }}
                >
                    过滤后打印
                </Button>
            </div>
        ),
        okButtonProps: {
            hidden: true,
        },
        cancelButtonProps: {
            hidden: true,
        },
    };
}

/**
 * 执行过滤和提示操作
 */
async function execFilterAndTip(
    orders: OrderDTO[],
    isSingleOrder: boolean,
    omits: {
        match: (value: OrderDTO) => boolean | Promise<boolean>;
        keyword: string;
    }[],
    buildTip: (keyword) => string,
    buildMoreOrderTip: (keyword) => string,
) {
    const keywordMap: Record<string, boolean> = {};

    const omitOrders: OrderDTO[] = [];

    for (let i = 0; i < orders.length; i++) {
        const order = orders[i];

        let flags: boolean[] = [];
        for (let j = 0; j < omits.length; j++) {
            const { match, keyword } = omits[j];

            const matchFlag = await match(order);

            flags.push(matchFlag);
            if (matchFlag) {
                keywordMap[keyword] = true;
            }
        }

        if (!flags.some((it) => it === true)) {
            omitOrders.push(order);
        }
    }

    const keyword = Object.keys(keywordMap).join("、");

    let showTip = omitOrders.length !== orders.length;

    if (!showTip) {
        return orders;
    }

    if (omitOrders.length === 0 || isSingleOrder) {
        const shouldNext = await new Promise<boolean>((yes) => {
            Modal.confirm({
                title: buildTip(keyword),
                okText: "继续打印",
                cancelText: "取消",
                onOk() {
                    yes(true);
                },
                onCancel() {
                    yes(false);
                },
            });
        });

        if (!shouldNext) {
            return [];
        }

        return orders;
    } else {
        const result = await new Promise<{ shouldNext: boolean; useOmit?: boolean }>((yes) => {
            const instance = Modal.confirm({
                title: buildMoreOrderTip(keyword),
                ...buildCustomConfirmProps({
                    getModalInstance() {
                        return instance;
                    },
                    onNext() {
                        yes({ shouldNext: true, useOmit: false });
                    },
                    onFilterNext() {
                        yes({ shouldNext: true, useOmit: true });
                    },
                }),
                onCancel() {
                    yes({ shouldNext: false });
                },
            });
        });

        if (!result.shouldNext) {
            return [];
        }

        if (!result.useOmit) {
            return orders;
        } else {
            return omitOrders;
        }
    }
}

const usePrintWaybillClick = (props: UsePrintWaybillClickParams) => {
    const {
        getSelectedOrders,
        onShipTaskDone,
        onUpdateOrder,
        multiPlatformModalOrderOriginCheck,
        onRefreshOrders,
        onUpdateOrderSellerRemarks,
        onUpdateSupplierOrderRemarks,
        showProcessInRange,
        supplierPartnershipsStore,
    } = props;
    const globalStore = useGlobalStore();
    const modalStore = useModalStore();

    const orderSettingStore = useOrderSettingStore();

    const { hasAuthority, openAuthorityModal } = useAuthorityCheck(Authority.ROLE_ORDER_PRINT);

    /**
     * 这里是用于埋点作用，不一定有值
     */
    const orderManagementStore = useOrderManagementStore();

    const getSafeSelectedOrders = async (): Promise<OrderDTO[]> => {
        const selectedOrders = getSelectedOrders();

        if (!selectedOrders || selectedOrders.length === 0) {
            return [];
        }

        const wiredOrders: OrderDTO[] = [];
        const safeOrders: OrderDTO[] = [];

        for (const order of selectedOrders) {
            if (order._weirdInfo) {
                wiredOrders.push(order);
            } else {
                safeOrders.push(order);
            }
        }

        if (!wiredOrders.length) {
            return selectedOrders;
        } else {
            try {
                const content = wiredOrders.map(({ _weirdInfo }) => _weirdInfo).join("\n");
                postLogInfo(
                    {
                        tag: LoggerInfoTag.ORDER_DATA_ERROR,
                        content,
                    },
                    { hideModel: true },
                );
            } catch {}

            /** 所选的订单都为数据错误的订单 */
            if (selectedOrders.length === wiredOrders.length) {
                Modal.error({
                    title: "订单数据异常，暂时无法打印，请联系客服处理数据异常订单",
                    okText: "联系客服",
                    okCancel: true,
                    cancelText: "关闭",
                    onOk: () => openUrl("https://v2.fahuoyi.com/cs"),
                });

                return [];
            } else {
                return await new Promise((yes, no) => {
                    modalStore.openModal(WiredOrderTipModal, {
                        onStop() {
                            yes([]);
                        },
                        onNext() {
                            yes(safeOrders);
                        },
                        wiredOrders,
                    });
                });
            }
        }
    };

    /**
     * @param defaultTemplateId 默认模板id 如快递分区绑定的模板
     */
    const handlePrint = async (defaultTemplateId?: number) => {
        if (!hasAuthority) {
            openAuthorityModal();

            return;
        }

        const safeSelectedOrders = await getSafeSelectedOrders();

        if (!safeSelectedOrders || safeSelectedOrders.length === 0) {
            return;
        }

        const shopIds = uniq(safeSelectedOrders.map((it) => it.shopId));
        const shops = shopIds.map((shopId) => globalStore.shopMapping.get(shopId));

        // 校验是否开启自动打印
        let next = await checkShopIdsEnabledAutoPrint(shops, safeSelectedOrders.length === 1);

        if (!next) {
            return;
        }

        const filterOrders = await checkBeforePrint(
            safeSelectedOrders,
            orderSettingStore.addressFilterKeywords,
            orderSettingStore.undeliverableAreaCodes,
            globalStore,
            modalStore,
        );

        if (!filterOrders?.length) {
            return;
        }

        if (supplierPartnershipsStore) {
            const supplierShopIds = uniq(
                filterOrders.filter((order) => order.supplierDropShippingInfo).map((order) => order.shopId),
            );

            await Promise.all(
                supplierShopIds.map((shopId) => supplierPartnershipsStore.ensureEstablishedPartnerships(shopId)),
            );
        }

        const multiContextShopId = shopIds.length === 1 ? shopIds[0] : undefined;

        if (multiPlatformModalOrderOriginCheck) {
            modalStore.openModal(MultiPlatformPrintWaybillModal, {
                orders: filterOrders,
                onShipTaskDone,
                defaultTemplateId,
                onUpdateOrder,
                multiPlatformModalOrderOriginCheck,
                onUpdateOrderSellerRemarks,
                onUpdateSupplierOrderRemarks,
                shopId: multiContextShopId,
                showProcessInRange,
                supplierPartnershipsStore,
            });
        } else {
            const _hasAliOutOrder = isAnyOrderWithAliOutTag(filterOrders);
            const _hasOtherDropShippingSupplierSourceOrder =
                globalStore.companyRoleStore.isSupplier &&
                filterOrders.some((order) => {
                    const shopType = globalStore.shopMapping.get(order.shopId)?.type;

                    return (
                        shopType === ShopType.OTHER_DROP_SHIPPING_SUPPLIER &&
                        isSupportedOtherDropShippingSupplierSourceShopType(order.sourceShopType)
                    );
                });

            if (_hasAliOutOrder || _hasOtherDropShippingSupplierSourceOrder) {
                modalStore.openModal(MultiPlatformPrintWaybillModal, {
                    orders: filterOrders,
                    onShipTaskDone,
                    defaultTemplateId,
                    onUpdateOrder,
                    onUpdateOrderSellerRemarks,
                    onUpdateSupplierOrderRemarks,
                    shopId: multiContextShopId,
                    showProcessInRange,
                    supplierPartnershipsStore,
                });
            } else {
                modalStore.openModal(PrintWaybillModal, {
                    orders: filterOrders,
                    onShipTaskDone,
                    defaultTemplateId,
                    onUpdateOrder,
                    onRefreshOrders,
                    onUpdateOrderSellerRemarks,
                    onUpdateSupplierOrderRemarks,
                    supplierPartnershipsStore,
                });
            }
        }

        if (
            safeSelectedOrders.length > 500 ||
            [OrderTab.LOGISTICS_WARNING, OrderTab.ABNORMAL].includes(orderManagementStore?.tab)
        ) {
            collectEvent(DataTrackEvent.PRINT_ORDERS, {
                order_count: safeSelectedOrders.length,
                shop_names: shops.map(({ name }) => name).join(),
                context: OrderTabNameMap[orderManagementStore?.tab],
            });
        }
    };

    return {
        handlePrint,
    };
};

export default usePrintWaybillClick;
